import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosError } from 'axios'
import { useToken } from '@/features/miiaFw/composables/useToken'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { GlobalError } from '@/utils/globalError/GlobalError'
import MiiaFwConfig from '@/configs/config'
import type { TokenInfo } from '@/features/miiaFw/types/cognito/auth'

export class ApiClient {
  private client: AxiosInstance
  private getTokens: () => TokenInfo | null

  constructor() {
    const { getTokens } = useToken()
    this.getTokens = getTokens
    this.client = axios.create({
      baseURL: `${MiiaFwConfig.config.apiGwEndpointPropscr}/api`,
      timeout: 2900
    })

    this.client.interceptors.request.use(
      config => {
        const accessToken = this.getTokens()?.access_token ?? ''
        if (accessToken) {
          config.headers = config.headers ?? {}
          config.headers['Authorization'] = `Bearer ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error)
    )
  }

  public request<T>(config: AxiosRequestConfig): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.client
        .request<T>(config)
        .then(response => {
          resolve(response.data)
        })
        .catch((error: unknown) => {
          if (isAxiosError(error)) {
            if (!error.response) {
              reject(
                new GlobalError(ERROR_CODES._811010_API_EXECUTION_FAILED, {
                  message: 'Network or timeout error'
                })
              )
            } else {
              reject(
                new GlobalError(ERROR_CODES._811010_API_EXECUTION_FAILED, {
                  message: `API Error: ${error.response.status}`
                })
              )
            }
          } else {
            reject(
              new GlobalError(ERROR_CODES._811010_API_EXECUTION_FAILED, {
                message: 'Unexpected error occurred'
              })
            )
          }
        })
    })
  }
}

function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  )
}
